import { useRouter } from "next/router";
import { useState } from "react";
import { Snackbar } from "@heathmont/moon-core-tw";
import { useProjects } from "@/contexts/projects/ProjectsContext";
import { handleProjectDeletionEvent } from "@/lib/analytics/savedResources";
import DeleteIcon from "../icons/Delete";
import XIcon from "../icons/XIcon";
import Modal from "../Modal";
import { ErrorToast } from "../navigator/ErrorToast";

const ProjectDeletionModal = () => {
  const { push } = useRouter();
  const {
    projectForDeletion,
    clearStagedProjectForDeletion,
    deleteProject,
    getProject,
  } = useProjects();
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [isErrorToastOpen, setIsErrorToastOpen] = useState(false);

  const handleDeleteClick = (projectId: string) => async () => {
    handleProjectDeletionEvent(getProject(projectId)!);
    try {
      await deleteProject(projectId);
      await push("/account/projects");
      clearStagedProjectForDeletion();
      setShowSuccessToast(true);
    } catch (e) {
      console.error(e);
      setIsErrorToastOpen(true);
    }
  };

  return (
    <>
      <Modal
        contentLabel="Delete project"
        isOpen={!!projectForDeletion}
        onClose={clearStagedProjectForDeletion}
      >
        {!!projectForDeletion && (
          <div className="relative max-h-[calc(100vh-32px)] w-[540px] max-w-[calc(100vw-32px)] rounded-3xl bg-white shadow-[0_10px_10px_rgba(0,12,16,0.10)]">
            <button
              className="absolute right-3 top-3 rounded-full p-1 hover:bg-black/5 active:bg-black/10"
              aria-label="Close"
              onClick={clearStagedProjectForDeletion}
            >
              <XIcon />
            </button>
            <div className="flex flex-col items-center">
              <h2 className="h-14 w-full border-b border-b-tab-border py-3 text-center font-roobert text-xl font-medium">
                Delete project
              </h2>
              <DeleteIcon className="mt-10 h-10 w-10" fill="#D34327" />
              <p className="w-full border-b border-b-tab-border pb-14 pt-4 text-center leading-5">
                <span className="block font-semibold text-[#D34327]">
                  Are you sure you want to delete this project?
                </span>
                <span>
                  The project will be permanently removed from your projects
                  page.
                </span>
              </p>
              <div className="flex w-full justify-end gap-2 p-4">
                <button
                  onClick={clearStagedProjectForDeletion}
                  className="flex h-12 w-[100px] items-center justify-center rounded-[100px] border border-tab-border font-semibold text-BG-60 hover:bg-tab-bg-hover active:bg-tab-bg-press"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteClick(projectForDeletion)}
                  className="flex h-12 w-[100px] items-center justify-center rounded-[100px] bg-[#D34327] font-semibold text-white hover:bg-[#b43820] active:bg-[#98301b]"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
      <SuccessToast
        isOpen={showSuccessToast}
        handleClose={() => setShowSuccessToast(false)}
      />
      <ErrorToast
        isOpen={isErrorToastOpen}
        handleClose={() => setIsErrorToastOpen(false)}
      />
    </>
  );
};

export default ProjectDeletionModal;

type SuccessToastProps = {
  isOpen: boolean;
  handleClose: () => void;
};

export const SuccessToast = ({ isOpen, handleClose }: SuccessToastProps) => {
  return (
    <Snackbar
      isOpen={isOpen}
      position="bottom-center"
      onOpenChange={handleClose}
      autoClose={1000}
      className="max-w-sm rounded-xl border border-BG-60 bg-white p-4 pr-6 text-black !shadow-[0_8px_8px_rgba(0,12,18,0.24)]"
    >
      <Snackbar.Icon>
        <DeleteIcon />
      </Snackbar.Icon>
      <Snackbar.Content>
        <p className="text-dark-color">Project deleted</p>
      </Snackbar.Content>
    </Snackbar>
  );
};
